import React, { useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Button, Stack, Typography, Paper,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';

import { Container, LoadingState, ErrorState } from '@/components/common';
import { useFaqDetail, useDeleteFaqPost } from '@/hooks/boards.hook';
import { successToast, errorToast, confirmToast } from '@/common/toast';
import { formatDate } from '@/util/dateUtils';

/**
 * FAQ 상세 페이지
 */
const FaqDetailPage = () => {
  const { faqId } = useParams();
  const navigate = useNavigate();

  const { data: faq, isLoading, error } = useFaqDetail(faqId);
  const deleteFaqMutation = useDeleteFaqPost();

  const handleDelete = useCallback(() => {
    confirmToast('이 FAQ를 삭제하시겠습니까?', async () => {
      try {
        await deleteFaqMutation.mutateAsync(faqId);
        successToast('FAQ가 삭제되었습니다.');
        navigate('/community/faq');
      } catch (err) {
        errorToast(`FAQ 삭제에 실패했습니다. - ${err.response?.data?.msg}`);
      }
    });
  }, [faqId, deleteFaqMutation, navigate]);

  const handleEdit = useCallback(() => {
    navigate(`/community/faq/edit/${faqId}`);
  }, [faqId, navigate]);

  if (isLoading) {
    return <LoadingState title="FAQ 로딩 중" />;
  }

  if (error || !faq) {
    return (
      <ErrorState
        title="FAQ 로딩 오류"
        message="FAQ 정보를 불러오는 중 오류가 발생했습니다."
        onGoBack={() => navigate('/community/faq')}
      />
    );
  }

  const title = faq.title ?? faq.question ?? '-';
  const content = faq.content ?? faq.answer ?? '';
  const author = faq.author ?? faq.userId ?? '-';
  const categoryName = [faq.category?.categoryType ?? faq.categoryType, faq.category?.categoryName ?? faq.categoryName].filter(Boolean).join(' - ') || '-';
  const hitCount = faq.hitCount ?? 0;
  const createdAt = faq.createdAt ?? faq.regDate ?? '-';
  const reply = faq.reply ?? null;

  return (
    <Container title="FAQ 상세">
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/community/faq')}
        >
          목록으로
        </Button>
        <Stack direction="row" gap={1}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleEdit}
          >
            수정
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
            disabled={deleteFaqMutation.isPending}
          >
            삭제
          </Button>
        </Stack>
      </Stack>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          {title}
        </Typography>
        <Stack direction="row" gap={2} flexWrap="wrap" sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            등록일: {formatDate(createdAt)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            작성자: {author}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            카테고리: {categoryName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            조회수: {hitCount}
          </Typography>
        </Stack>
        <Box
          sx={{
            py: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            whiteSpace: 'pre-wrap',
          }}
        >
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            질문
          </Typography>
          {content || '-'}
        </Box>
        {reply && (
          <Box
            sx={{
              mt: 3,
              pt: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              whiteSpace: 'pre-wrap',
            }}
          >
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              답변
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {reply.author ?? reply.userId ?? '-'} · {formatDate(reply.createdAt)}
            </Typography>
            {reply.content ?? '-'}
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default FaqDetailPage;
